import { useQuery } from '@tanstack/react-query';
import { integrationApiService } from '../../../services/integrationApi';
import type { IntegrationConfig } from '../../../services/integrationApi';

export type ListingHold = Awaited<ReturnType<typeof integrationApiService.listShopifyListingHolds>>[number];

/** Query key of an integration's listing holds, shared with the dashboard and the old Mapping tab. */
export const listingHoldsKey = (integrationId: string) => ['shopify-listing-holds', integrationId] as const;

export interface ListingMatchesToConfirm {
  /** Products a sync refused to create because a Shopify listing might be the same item. */
  count: number;
  /** The server returns at most 50 holds, so the true count may be higher. */
  capped: boolean;
  isLoading: boolean;
}

/** Most holds `/integrations/:id/shopify-listing-holds` returns. */
const HOLD_PAGE = 50;

/**
 * The unresolved listing-match count for the shell's badges ("Listing matches ·
 * N to confirm"). It reads the same query as the view's To confirm card, so a
 * decision made there updates the badge. Always 0 for a non-Shopify integration.
 */
export function useListingMatchesToConfirm(
  integration: Pick<IntegrationConfig, 'id' | 'platform'>,
): ListingMatchesToConfirm {
  const isShopify = integration.platform === 'shopify';
  const { data, isLoading } = useQuery({
    queryKey: listingHoldsKey(integration.id),
    queryFn: () => integrationApiService.listShopifyListingHolds(integration.id),
    enabled: isShopify,
    staleTime: 30_000,
  });
  const count = isShopify ? data?.length ?? 0 : 0;
  return { count, capped: count >= HOLD_PAGE, isLoading: isShopify && isLoading };
}
